import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Global } from '@emotion/react';
import { globalStyles } from './styles/global.style';
import useTrimStore from './store/trimStore'; // 🔧

// Auth Components
import Login from './pages/Login/Login';
import Register from './pages/Register/Register';
import PrivacyPolicy from './pages/PrivacyPolicy/PrivacyPolicy'; 
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';

// Main Pages
import Upload from './pages/Upload/Upload';
import Trim from './pages/Trim/Trim';
import Generate from './pages/Generate/Generate';
import GenerateReport from './pages/GenerateReport/GenerateReport';
import Mapping from './pages/Mapping/Mapping';


// 🔧 확장: 경고창 기능이 추가된 라우트 변경 감지 컴포넌트
const RouteChangeHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { reset: resetTrimStore, hasUnsavedWork } = useTrimStore();
  
  // 이전 경로를 추적하기 위한 ref
  const previousPathRef = useRef(location.pathname);
  const isNavigationBlockedRef = useRef(false);

  const checkIfGenerateReportHasWork = () => {
    // 현재 페이지가 GenerateReport인지 확인하고, URL state가 있는지 체크
    return location.state?.inputDataInfo ? true : false;
  };


  // 🔧 Trim 페이지에서 작업 중인지 확인하는 함수
  const checkIfWorkInProgress = () => {
    const state = useTrimStore.getState();
    
    // 작업 중인 상태들을 확인
    const hasWork = (
      state.selectedFile ||                    // 파일이 선택됨
      state.treeData ||                       // 트리 분석 완료
      state.terminalNodes.length > 0 ||       // 터미널 노드 선택됨
      state.finalSelectedNodes.length > 0 ||  // 최종 선택 노드 있음
      state.previewData ||                    // 미리보기 데이터 있음
      state.outputFileName !== 'selected_data' // 기본값이 아닌 파일명
    );
    
    console.log('[ROUTE_CHANGE]', 'Work in progress check:', {
      selectedFile: !!state.selectedFile,
      treeData: !!state.treeData,
      terminalNodes: state.terminalNodes.length,
      finalSelectedNodes: state.finalSelectedNodes.length,
      previewData: !!state.previewData,
      customFileName: state.outputFileName !== 'selected_data',
      hasWork
    });
    
    return hasWork;
  };

  // 🔧 경고창 표시 및 사용자 확인
  const showNavigationWarning = (fromPage) => {
    return new Promise((resolve) => {
      const message = getWarningMessage(fromPage);
      const userConfirmed = window.confirm(message);
      console.log('[ROUTE_CHANGE]', `User confirmation for ${fromPage}:`, userConfirmed);
      resolve(userConfirmed);
    });
  };

  const checkIfTrimWorkInProgress = () => {
    const state = useTrimStore.getState();
    return (
      state.selectedFile ||
      state.treeData ||
      state.terminalNodes.length > 0 ||
      state.finalSelectedNodes.length > 0 ||
      state.previewData ||
      state.outputFileName !== 'selected_data'
    );
  };

  const getWarningMessage = (fromPage) => {
    if (fromPage === '/trim') {
      return (
        '데이터 편집 작업이 초기화됩니다.\n\n' +
        '• 선택한 파일 정보\n• 분석된 컬럼 구조\n• 선택한 컬럼들\n• 미리보기 데이터\n\n' +
        '정말로 이 페이지를 떠나시겠습니까?'
      );
    } else if (fromPage.includes('/generate/report')) {
      return (
        '보고서 생성 작업이 초기화됩니다.\n\n' +
        '• 선택한 데이터 파일들\n• 보고서 설정 정보\n• 입력한 제목 및 설정\n\n' +
        '정말로 이 페이지를 떠나시겠습니까?'
      );
    }
    return '작업 중인 내용이 초기화될 수 있습니다.\n정말로 이 페이지를 떠나시겠습니까?';
  };

  // 🔧 라우트 변경 처리
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;
    
    if (isNavigationBlockedRef.current) {
      isNavigationBlockedRef.current = false;
      return;
    }

    let shouldWarn = false;

    // Trim 페이지에서 나가는 경우
    if (previousPath === '/trim' && currentPath !== '/trim') {
      shouldWarn = checkIfTrimWorkInProgress();
    }
    
    // 🆕 GenerateReport 페이지에서 나가는 경우
    else if (previousPath.includes('/generate/report') && !currentPath.includes('/generate/report')) {
      console.log('[ROUTE_CHANGE]', 'Checking for unsaved work in GenerateReport page...');
      shouldWarn = checkIfGenerateReportHasWork();
    }

    if (shouldWarn) {
      isNavigationBlockedRef.current = true;
      navigate(previousPath, { replace: true });
      
      showNavigationWarning(previousPath).then((confirmed) => {
        if (confirmed) {
          // Trim 상태만 초기화 (GenerateReport는 URL state 기반이므로 별도 초기화 불필요)
          if (previousPath === '/trim') {
            resetTrimStore();
          }
          navigate(currentPath, { replace: true });
        }
        isNavigationBlockedRef.current = false;
      });
      
      return;
    }
    
    // 일반적인 Trim store 초기화
    if (currentPath !== '/trim') {
      resetTrimStore();
    }
    
    previousPathRef.current = currentPath;
    
  }, [location.pathname, navigate, resetTrimStore, location.state]);

  return null;
};

// 🔧 브라우저 새로고침/닫기 시에도 경고하는 Hook
const useBeforeUnload = () => {
  const location = useLocation();

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      let hasWork = false;
      let message = '';

      // Trim 페이지 경고 (기존)
      if (location.pathname === '/trim') {
        const state = useTrimStore.getState();
        hasWork = (
          state.selectedFile ||
          state.treeData ||
          state.terminalNodes.length > 0 ||
          state.finalSelectedNodes.length > 0 ||
          state.previewData ||
          state.outputFileName !== 'selected_data'
        );
        message = '데이터 편집 작업이 초기화될 수 있습니다. 정말로 페이지를 떠나시겠습니까?';
      }
      
      // 🆕 GenerateReport 페이지 경고
      else if (location.pathname.includes('/generate/report')) {
        // URL state가 있으면 작업 중인 것으로 간주
        hasWork = location.state?.inputDataInfo ? true : false;
        message = '보고서 생성 작업이 초기화될 수 있습니다. 정말로 페이지를 떠나시겠습니까?';
      }

      if (hasWork) {
        event.preventDefault();
        event.returnValue = message;
        return event.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location.pathname, location.state]); // 🔧 location.state 의존성 추가
};

// 🔧 두 Hook을 모두 사용하는 컴포넌트
const NavigationGuard = () => {
  useBeforeUnload(); // 브라우저 새로고침/닫기 경고
  return <RouteChangeHandler />; // 라우트 변경 경고
};

function App() {
  return (
    <BrowserRouter>
      <Global styles={globalStyles} />
      <div className='App'>
        <NavigationGuard /> {/* 🔧 수정: 통합된 가드 사용 */}
        
        <Routes>
          {/* Public Routes (Auth) */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          {/* Protected Routes */}
          <Route path="/upload" element={
            <ProtectedRoute>
              <Upload />
            </ProtectedRoute>
          } />
          <Route path="/trim" element={
            <ProtectedRoute>
              <Trim />
            </ProtectedRoute>
          } />
          <Route path="/generate" element={
            <ProtectedRoute>
              <Generate />
            </ProtectedRoute>
          } />
          <Route path="/generate/report" element={
            <ProtectedRoute>
              <GenerateReport />
            </ProtectedRoute>
          } />
          <Route path="/mapping" element={
            <ProtectedRoute>
              <Mapping />
            </ProtectedRoute>
          } />
          
          {/* Default redirects */}
          <Route path="/" element={<Navigate to="/upload" replace />} />
          <Route path="*" element={<Navigate to="/upload" replace />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;